import { memo } from "react"
import { FiEye, FiTrash2 } from "react-icons/fi"

import type { SessionResponse } from "@/types/session"
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface SessionCardProps {
  session: SessionResponse
  onEdit?: (session: SessionResponse) => void
  onDelete?: (session: SessionResponse) => void
  onView?: (session: SessionResponse) => void
}

const SessionCard = memo(
  ({ session, onEdit, onDelete, onView }: SessionCardProps) => {
    const titleValue = session.name ?? session.id

    const subtitleValue = session.slug ?? undefined

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Stack gap={1}>
          <Heading size="sm">{titleValue}</Heading>
          {subtitleValue !== undefined && (
            <Text color="fg.muted" fontSize="sm">
              {subtitleValue || "No description"}
            </Text>
          )}
        </Stack>

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>Created: {new Date(session.created_at).toLocaleString()}</Text>
          <Text>Updated: {new Date(session.updated_at).toLocaleString()}</Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex key="name" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.name, "str")}
            </Text>
          </Flex>
          <Flex key="slug" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Slug</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.slug, "str")}
            </Text>
          </Flex>
          <Flex
            key="description"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Description</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.description, "str")}
            </Text>
          </Flex>
          <Flex
            key="session_type"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Session Type</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.session_type, "str")}
            </Text>
          </Flex>
          <Flex
            key="start_week"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Start Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.start_week, "date")}
            </Text>
          </Flex>
          <Flex
            key="end_week"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">End Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.end_week, "date")}
            </Text>
          </Flex>
          <Flex
            key="registration_opens"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Registration Opens</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.registration_opens, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="registration_closes"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Registration Closes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.registration_closes, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="early_bird_deadline"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Early Bird Deadline</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.early_bird_deadline, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="makeup_classes_allowed"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Makeup Classes Allowed</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.makeup_classes_allowed, "int")}
            </Text>
          </Flex>
          <Flex
            key="no_class_dates"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">No Class Dates</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.no_class_dates, "dict")}
            </Text>
          </Flex>
          <Flex
            key="is_active"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Active</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.is_active, "bool")}
            </Text>
          </Flex>
          <Flex
            key="is_published"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Published</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.is_published, "bool")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(session.notes, "str")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end">
          {onView && (
            <Button size="sm" variant="ghost" onClick={() => onView(session)}>
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(session)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete session"
              onClick={() => onDelete(session)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

SessionCard.displayName = "SessionCard"

export default SessionCard
